/**
 * Breadcrumb tail slot — lets detail pages append the loaded entity name
 * (e.g. "Sale Assistant") as a third crumb after section / parent.
 *
 * The tail is a single string. Detail pages call `useBreadcrumbTail(name)`
 * once the entity is loaded; the hook clears the tail on unmount so list
 * pages don't inherit it.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

interface BreadcrumbTailValue {
  tail: string | null;
  setTail: (value: string | null) => void;
}

const BreadcrumbTailContext = createContext<BreadcrumbTailValue | null>(null);

export function BreadcrumbTailProvider({ children }: { children: ReactNode }) {
  const [tail, setTail] = useState<string | null>(null);
  return (
    <BreadcrumbTailContext.Provider value={{ tail, setTail }}>
      {children}
    </BreadcrumbTailContext.Provider>
  );
}

export function useBreadcrumbTailValue(): string | null {
  return useContext(BreadcrumbTailContext)?.tail ?? null;
}

/** Detail pages call this once their entity name is known. */
export function useBreadcrumbTail(name: string | null | undefined): void {
  const ctx = useContext(BreadcrumbTailContext);
  useEffect(() => {
    if (!ctx) return;
    ctx.setTail(name ?? null);
    return () => ctx.setTail(null);
  }, [ctx, name]);
}
